import { vehicleModelApi } from "#/api/vehicleModelApi";
import ActionButton from "#/components/buttons/ActionButton";
import {
  showSuccessNotification,
} from "#/components/notifications";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import type { CreateVehicleModelPayload, VehicleModel } from "#/types/vehicle-model";
import { Accordion, Button, MultiSelect, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import { useAllowedStatuses } from "#/hooks/useAllowedStatuses";
import { useClient } from "#/hook/useClient";
import { useUnsavedChangesWarning } from "#/hooks/useUnsavedChangesWarning";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { logger } from "#/utils/logger";
import {
  getUnitSystem,
  volumeToCanonical,
  volumeToDisplay,
  volumeUnitLabel,
  weightToCanonical,
  weightToDisplay,
  weightUnitLabel,
} from "#/utils/unitUtils";

/** The editable part of a vehicle model, as the form holds it. */
const toFormValues = (vehicleModel: VehicleModel): CreateVehicleModelPayload => ({
  name: vehicleModel.name ?? "",
  brand: vehicleModel.brand ?? "",
  model: vehicleModel.model ?? "",
  size: vehicleModel.size ?? "",
  // Decimal DB columns come back as fixed-precision strings (e.g.
  // "100.000") — cast to a clean number so the input doesn't show it.
  cargo_capacity:
    vehicleModel.cargo_capacity != null ? Number(vehicleModel.cargo_capacity) : undefined,
  max_payload: vehicleModel.max_payload != null ? Number(vehicleModel.max_payload) : undefined,
  allowed_statuses: vehicleModel.allowed_statuses ?? [],
  customize_columns: { ...(vehicleModel.customize_columns ?? {}) },
});

const VehicleModelDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { canWrite } = usePermission();
  const allowedStatuses = useAllowedStatuses();
  const { client } = useClient();
  const unitSystem = getUnitSystem(client);

  const form = useForm<CreateVehicleModelPayload>({
    initialValues: {
      name: "",
      brand: "",
      model: "",
      size: "",
      cargo_capacity: undefined,
      max_payload: undefined,
      allowed_statuses: [],
      customize_columns: {},
    },
    validate: {
      name: (value) => (value ? null : t("vehicleModels.validation.nameRequired")),
      brand: (value) => (value ? null : t("vehicleModels.validation.brandRequired")),
      model: (value) => (value ? null : t("vehicleModels.validation.modelRequired")),
      size: (value) => (value ? null : t("vehicleModels.validation.sizeRequired")),
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    const payload = {
      ...values,
      // Decimal DB columns come back from the API as strings (e.g. "100.000");
      // re-cast here so an untouched field never gets resubmitted as a string.
      // `null` (not `undefined`) so an explicit clear actually persists —
      // `undefined` keys get dropped by JSON and never reach the backend.
      cargo_capacity: values.cargo_capacity != null ? Number(values.cargo_capacity) : null,
      max_payload: values.max_payload != null ? Number(values.max_payload) : null,
    };

    vehicleModelMutation.mutate(payload);
  };

  const vehicleModelMutation = useMutation({
    mutationFn: async (payload: CreateVehicleModelPayload) => {
      if (id !== "new") {
        return await vehicleModelApi.update(Number(id), payload);
      } else {
        return await vehicleModelApi.create(payload);
      }
    },
    onSuccess: (data) => {
      showSuccessNotification({
        message: id === "new" ? t("vehicleModels.createdSuccess") : t("vehicleModels.updatedSuccess"),
      });
      if (id === "new") {
        void navigate(`/fleet/models/${data.data.id}`, { replace: true });
      } else {
        // The form now matches what is stored: make that its clean baseline,
        // then let the refetched row flow in through the apply effect.
        const saved = toFormValues(data.data);
        form.setInitialValues(saved);
        form.resetDirty(saved);
        void queryClient.invalidateQueries({ queryKey: ["vehicleModelDetails", id] });
      }
    },
    onError: (error) => {
      // The global API interceptor toasts the backend message (e.g. the 409 for
      // a duplicate name); a second toast here would only repeat it.
      logger.error("VehicleModelDetail save error:", error);
    },
  });

  const { data: vehicleModel } = useQuery({
    queryKey: ["vehicleModelDetails", id],
    enabled: !!id && id !== "new",
    queryFn: async () => {
      if (!id || id === "new") throw new Error("Invalid vehicleModelId");

      const response = await vehicleModelApi.getById(Number(id));
      if (!response.data) throw new Error("Failed to fetch vehicle model");
      return response.data;
    },
  });

  // Load the fetched model into the form, but never over unsaved edits: React
  // Query refetches on window focus, and that used to wipe what the user was
  // typing. Each fetched object is applied once; while the form is dirty it is
  // left alone and picked up after the next save makes the form clean again.
  const appliedModelRef = useRef<VehicleModel | null>(null);
  useEffect(() => {
    if (!vehicleModel || appliedModelRef.current === vehicleModel || form.isDirty()) return;
    const values = toFormValues(vehicleModel);
    form.setInitialValues(values);
    form.setValues(values);
    form.resetDirty(values);
    appliedModelRef.current = vehicleModel;
  }, [vehicleModel, form]);

  useUnsavedChangesWarning(form.isDirty());

  return (
    <DetailPageLayout
      title={id !== "new" ? t("vehicleModels.modelDetails") : t("vehicleModels.createModel")}
      navRightSection={
        <div className="w-full flex justify-end gap-2 items-center">
          <Button
            color="black"
            variant="outline"
            type="reset"
            disabled={!form.isDirty()}
            onClick={() => {
              form.reset();
            }}
            size="xs"
          >
            {t("common.clear")}
          </Button>

          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              variant="filled"
              loading={vehicleModelMutation.isPending}
              disabled={!form.isDirty() || !canWrite()}
              onClick={() => {
                form.onSubmit(handleSubmit)();
              }}
              size="xs"
            >
              {t("common.save")}
            </Button>
          </PermissionTooltip>
        </div>
      }
    >
      <div>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Accordion
            variant="separated"
            defaultValue={["basic", "customize_columns"]}
            multiple
          >
            <Accordion.Item value="basic">
              <Accordion.Control>{t("common.basicInformation")}</Accordion.Control>
              <Accordion.Panel>
                <div className="w-full flex flex-col gap-4">
                  <TextInput
                    label={t("vehicleModels.form.name")}
                    withAsterisk
                    disabled={!canWrite()}
                    placeholder={t("vehicleModels.form.namePlaceholder")}
                    {...form.getInputProps("name")}
                  />

                  <div className="w-full grid grid-cols-2 gap-4">
                    <TextInput
                      label={t("vehicleModels.form.brand")}
                      withAsterisk
                      disabled={!canWrite()}
                      placeholder={t("vehicleModels.form.brandPlaceholder")}
                      {...form.getInputProps("brand")}
                    />

                    <TextInput
                      label={t("vehicleModels.form.model")}
                      withAsterisk
                      disabled={!canWrite()}
                      placeholder={t("vehicleModels.form.modelPlaceholder")}
                      {...form.getInputProps("model")}
                    />
                  </div>

                  {/* The three measurements together, one hint under each. */}
                  <div className="w-full grid grid-cols-3 gap-4">
                    <TextInput
                      label={t("vehicleModels.form.size")}
                      description={t("vehicleModels.form.sizeHint")}
                      withAsterisk
                      disabled={!canWrite()}
                      placeholder={t("vehicleModels.form.sizePlaceholder")}
                      {...form.getInputProps("size")}
                    />

                    <TextInput
                      label={`${t("vehicleModels.form.cargoCapacity")} (${volumeUnitLabel(unitSystem)})`}
                      description={t("vehicleModels.form.cargoCapacityHint")}
                      type="number"
                      disabled={!canWrite()}
                      value={
                        form.values.cargo_capacity != null
                          ? (volumeToDisplay(form.values.cargo_capacity, unitSystem) ?? "")
                          : ""
                      }
                      onChange={(event) => {
                        const raw = event.currentTarget.value;
                        form.setFieldValue(
                          "cargo_capacity",
                          raw === "" ? undefined : volumeToCanonical(Number(raw), unitSystem)
                        );
                      }}
                    />

                    <TextInput
                      label={`${t("vehicleModels.form.maxPayload")} (${weightUnitLabel(unitSystem)})`}
                      description={t("vehicleModels.form.maxPayloadHint")}
                      type="number"
                      disabled={!canWrite()}
                      value={
                        form.values.max_payload != null
                          ? (weightToDisplay(form.values.max_payload, unitSystem) ?? "")
                          : ""
                      }
                      onChange={(event) => {
                        const raw = event.currentTarget.value;
                        form.setFieldValue(
                          "max_payload",
                          raw === "" ? undefined : weightToCanonical(Number(raw), unitSystem)
                        );
                      }}
                    />
                  </div>

                  <MultiSelect
                    label={t("vehicleModels.form.allowedStatuses")}
                    description={t("vehicleModels.form.allowedStatusesHint")}
                    placeholder={t("common.selectStatus")}
                    searchable
                    disabled={!canWrite()}
                    data={allowedStatuses.map((status) => ({
                      value: status,
                      label: sanitizeItemStatusLabel(status),
                    }))}
                    {...form.getInputProps("allowed_statuses")}
                  />
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="customize_columns">
              <Accordion.Control>
                <div className="flex items-center gap-2">
                  <span>{t("common.customFields")}</span>
                  <PermissionTooltip disabled={!canWrite()}>
                    <ActionButton
                      color="black"
                      size="xs"
                      icon={IconPlus}
                      onClick={(e) => {
                        e.stopPropagation();
                        const existing = { ...form.values.customize_columns };
                        let newKey = `field_${Object.keys(existing).length + 1}`;
                        while (newKey in existing) {
                          newKey += "_";
                        }
                        form.setFieldValue("customize_columns", {
                          ...existing,
                          [newKey]: "",
                        });
                      }}
                      disabled={!canWrite()}
                    />
                  </PermissionTooltip>
                </div>
              </Accordion.Control>

              <Accordion.Panel>
                {Object.entries(form.values.customize_columns ?? {}).length <=
                0 ? (
                  <div>{t("common.addCustomFieldHint")}</div>
                ) : (
                  <div className="w-full flex flex-col gap-2">
                    {Object.entries(form.values.customize_columns ?? {}).map(
                      ([key, value], index) => {
                        const fieldId = `${key}_${index}`;

                        return (
                          <div
                            key={fieldId}
                            className="w-full grid grid-cols-[auto_1fr_1fr_auto] gap-2 items-center"
                          >
                            <span className="bg-black/5 font-bold text-black/70 rounded-full p-2 w-8 h-8 flex items-center justify-center">
                              <span>{index + 1}</span>
                            </span>
                            <TextInput
                              defaultValue={key}
                              placeholder={t("common.fieldName")}
                              disabled={!canWrite()}
                              onBlur={(event) => {
                                const newKey = event.currentTarget.value.trim();
                                if (!newKey || newKey === key) return;

                                const updated = {
                                  ...form.values.customize_columns,
                                };
                                const val = updated[key];
                                delete updated[key];

                                if (newKey in updated) return;

                                updated[newKey] = val;
                                form.setFieldValue("customize_columns", updated);
                              }}
                            />

                            <TextInput
                              value={value}
                              placeholder={t("common.fieldValue")}
                              disabled={!canWrite()}
                              onChange={(event) => {
                                form.setFieldValue(
                                  `customize_columns.${key}`,
                                  event.currentTarget.value
                                );
                              }}
                            />

                            <PermissionTooltip disabled={!canWrite()}>
                              <ActionButton
                                icon={IconTrash}
                                variant="white"
                                color="red.5"
                                onClick={() => {
                                  const updated = {
                                    ...form.values.customize_columns,
                                  };
                                  delete updated[key];
                                  form.setFieldValue("customize_columns", updated);
                                }}
                                disabled={!canWrite()}
                              />
                            </PermissionTooltip>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </form>
      </div>
    </DetailPageLayout>
  );
};

export default VehicleModelDetailPage;
